import { useState, useEffect, useCallback } from 'react';
import { SupportNetworkMember } from '../appTypes';
import { apiGet, API_BASE, fetchWithRetry } from '../appUtils';
import { enqueue, isOfflineable } from '../offlineQueue';
import { toast } from 'sonner';

export function useSupportNetwork(authToken: string | null) {
  const [networkView, setNetworkView] = useState<'map' | 'list'>('map');
  const [networkSelectedId, setNetworkSelectedId] = useState<number | null>(null);
  const [networkMembers, setNetworkMembers] = useState<SupportNetworkMember[]>([]);
  const [networkLoading, setNetworkLoading] = useState(false);
  const [showNetworkForm, setShowNetworkForm] = useState(false);
  const [networkFormMode, setNetworkFormMode] = useState<'add' | 'edit'>('add');
  const [networkEditTarget, setNetworkEditTarget] = useState<SupportNetworkMember | null>(null);
  const [networkFormName, setNetworkFormName] = useState('');
  const [networkFormRelationship, setNetworkFormRelationship] = useState('');
  const [networkFormRing, setNetworkFormRing] = useState<'inner' | 'middle' | 'outer'>('middle');
  const [networkFormPhone, setNetworkFormPhone] = useState('');
  const [networkFormNotes, setNetworkFormNotes] = useState('');
  const [networkFormEmoji, setNetworkFormEmoji] = useState('🤝');
  const [networkFormColor, setNetworkFormColor] = useState('#8b6fb0');
  const [networkFormSaving, setNetworkFormSaving] = useState(false);

  const loadSupportNetwork = useCallback(async () => {
    if (!authToken) return;
    setNetworkLoading(true);
    try {
      const remote = await apiGet<SupportNetworkMember[]>('/support-network', authToken);
      if (remote) setNetworkMembers(remote);
    } finally {
      setNetworkLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    if (!authToken) return;
    loadSupportNetwork();
  }, [authToken, loadSupportNetwork]);

  async function saveSupportNetworkMember() {
    if (!networkFormName.trim()) return;
    setNetworkFormSaving(true);
    try {
      const body = {
        name: networkFormName.trim(),
        relationship: networkFormRelationship.trim() || undefined,
        ring: networkFormRing,
        phone: networkFormPhone.trim() || undefined,
        notes: networkFormNotes.trim() || undefined,
        emoji: networkFormEmoji,
        color: networkFormColor,
      };
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      if (networkFormMode === 'add') {
        try {
          const res = await fetchWithRetry(`${API_BASE}/support-network`, { method: 'POST', headers, body: JSON.stringify(body) });
          if (res.ok) {
            const inserted = await res.json() as SupportNetworkMember;
            setNetworkMembers(prev => [inserted, ...prev]);
            setNetworkSelectedId(inserted.id);
          } else {
            toast.error("Couldn't save. Try again.");
          }
        } catch {
          if (isOfflineable('/api/support-network')) {
            enqueue({ method: 'POST', path: `${API_BASE}/support-network`, body, token: authToken ?? null });
            toast('Saved offline \u2014 will sync when you reconnect', { icon: '\u2601\ufe0f' });
          } else {
            toast.error("Couldn't reach the server. This change wasn't saved — try again when you have a better connection.");
          }
        }
      } else if (networkEditTarget) {
        const editPath = `/api/support-network/${networkEditTarget.id}`;
        try {
          const res = await fetchWithRetry(`${API_BASE}/support-network/${networkEditTarget.id}`, { method: 'PUT', headers, body: JSON.stringify(body) });
          if (res.ok) {
            const updated = await res.json() as SupportNetworkMember;
            setNetworkMembers(prev => prev.map(m => m.id === updated.id ? updated : m));
          } else {
            toast.error("Couldn't save. Try again.");
          }
        } catch {
          if (isOfflineable(editPath)) {
            enqueue({ method: 'PUT', path: `${API_BASE}/support-network/${networkEditTarget.id}`, body, token: authToken ?? null });
            toast('Saved offline \u2014 will sync when you reconnect', { icon: '\u2601\ufe0f' });
          } else {
            toast.error("Couldn't reach the server. This change wasn't saved — try again when you have a better connection.");
          }
        }
      }
      setShowNetworkForm(false);
      resetNetworkForm();
    } finally {
      setNetworkFormSaving(false);
    }
  }

  async function deleteSupportNetworkMember(id: number) {
    const headers: HeadersInit = {};
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    try {
      const res = await fetchWithRetry(`${API_BASE}/support-network/${id}`, { method: 'DELETE', headers });
      if (res.ok) {
        setNetworkMembers(prev => prev.filter(m => m.id !== id));
        if (networkSelectedId === id) setNetworkSelectedId(null);
      } else {
        toast.error("Couldn't remove. Try again.");
      }
    } catch {
      toast.error("Couldn't reach the server. Check your connection and try again.");
    }
  }

  function resetNetworkForm() {
    setNetworkFormName('');
    setNetworkFormRelationship('');
    setNetworkFormRing('middle');
    setNetworkFormPhone('');
    setNetworkFormNotes('');
    setNetworkFormEmoji('🤝');
    setNetworkFormColor('#8b6fb0');
    setNetworkEditTarget(null);
    setNetworkFormMode('add');
  }

  function openNetworkEditForm(member: SupportNetworkMember) {
    setNetworkFormMode('edit');
    setNetworkEditTarget(member);
    setNetworkFormName(member.name);
    setNetworkFormRelationship(member.relationship ?? '');
    setNetworkFormRing((member.ring as 'inner' | 'middle' | 'outer') ?? 'middle');
    setNetworkFormPhone(member.phone ?? '');
    setNetworkFormNotes(member.notes ?? '');
    setNetworkFormEmoji(member.emoji);
    setNetworkFormColor(member.color);
    setShowNetworkForm(true);
  }

  return {
    networkView,
    setNetworkView,
    networkSelectedId,
    setNetworkSelectedId,
    networkMembers,
    setNetworkMembers,
    networkLoading,
    showNetworkForm,
    setShowNetworkForm,
    networkFormMode,
    setNetworkFormMode,
    networkEditTarget,
    networkFormName,
    setNetworkFormName,
    networkFormRelationship,
    setNetworkFormRelationship,
    networkFormRing,
    setNetworkFormRing,
    networkFormPhone,
    setNetworkFormPhone,
    networkFormNotes,
    setNetworkFormNotes,
    networkFormEmoji,
    setNetworkFormEmoji,
    networkFormColor,
    setNetworkFormColor,
    networkFormSaving,
    loadSupportNetwork,
    saveSupportNetworkMember,
    deleteSupportNetworkMember,
    resetNetworkForm,
    openNetworkEditForm,
  };
}
